<template>
  <div ref="pageEl" class="flex flex-col h-screen bg-white dark:bg-[#0d1117] overflow-hidden">
    <CalendarHeader
      :current-view="store.currentView"
      :selected-date="selectedDateObj"
      :connection-status="store.connectionStatus"
      :is-fullscreen="isFullscreen"
      @prev="navigatePrev"
      @next="navigateNext"
      @today="navigateToday"
      @view-change="store.setView"
      @open-settings="store.openSettingsPanel"
      @fullscreen="onFullscreen"
      @toggle-sidebar="toggleSidebar"
      @select-search-result="onSelectSearchResult"
      @search-submit="onSearchSubmit"
      @close-search-overlay="store.closeSearchOverlay"
      @search-filter-change="onSearchFilterChange"
    />

    <div class="flex flex-1 overflow-hidden">
      <div
        class="flex-shrink-0 overflow-hidden h-full"
        :class="isResizing ? '' : 'transition-[width] duration-300 ease-in-out'"
        :style="{ width: sidebarOpen ? sidebarWidth + 'px' : '0px' }"
      >
        <!-- Fixed inner width so content slides out instead of squishing -->
        <div class="h-full" :style="{ width: sidebarWidth + 'px' }">
          <CalendarSidebar
            :selected-date="selectedDateObj"
            :calendars="store.activeConnectedCalendars"
            :visible-calendar-ids="store.visibleCalendarIds"
            :loading="store.connectedCalendarsLoading"
            @create="store.openBookingSlideOver(null)"
            @date-select="store.setDate"
            @toggle-calendar="store.toggleCalendarVisibility"
          />
        </div>
      </div>

      <!-- Drag handle -->
      <div
        v-if="sidebarOpen"
        class="w-1 flex-shrink-0 cursor-col-resize relative group z-10"
        @mousedown.prevent="startResize"
      >
        <div class="absolute inset-y-0 -left-1 -right-1 group-hover:bg-blue-400/40 transition-colors duration-150"></div>
      </div>

      <main
        ref="mainEl"
        class="flex-1 overflow-hidden flex flex-col bg-white dark:bg-[#0d1117] min-w-0"
        :class="fullscreenMode === 'main' ? 'pt-[2%] pl-[2%]' : ''"
      >
        <!-- Search results overlay (Enter in the header search box) -->
        <CalendarSearchResultsOverlay
          v-if="store.searchOverlayOpen"
          :query="store.searchOverlayQuery"
          :results="store.searchOverlayResults"
          :loading="store.searchOverlayLoading"
          @close="store.closeSearchOverlay"
          @select="onSelectSearchResult"
        />

        <!-- Error state -->
        <div v-else-if="store.bookingsError" class="flex flex-col items-center justify-center h-full text-center px-4">
          <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.bookingsError }}</p>
          <button @click="retry" class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
            Try again
          </button>
        </div>

        <!-- Grid views -->
        <template v-else>
          <CalendarWeekView
            v-if="store.currentView === 'week'"
            :selected-date="selectedDateObj"
            @event-click="onEventClick"
            @slot-click="onSlotClick"
            @day-click="store.setDate"
          />
          <CalendarDayView
            v-else-if="store.currentView === 'day'"
            :selected-date="selectedDateObj"
            @event-click="onEventClick"
            @slot-click="onSlotClick"
          />
          <Calendar4DayView
            v-else-if="store.currentView === '4days'"
            :selected-date="selectedDateObj"
            @event-click="onEventClick"
            @slot-click="onSlotClick"
            @day-click="store.setDate"
          />

          <!-- Month + year — animated slide transition on period change -->
          <div
            v-else-if="store.currentView === 'month' || store.currentView === 'year'"
            class="relative flex-1 overflow-hidden h-full"
          >
            <Transition :name="'cal-slide-' + navDirection">
              <CalendarMonthView
                v-if="store.currentView === 'month'"
                :key="monthKey"
                :selected-date="selectedDateObj"
                class="absolute inset-0"
                @event-click="onEventClick"
                @day-click="drillDown"
              />
              <CalendarYearView
                v-else
                :key="yearKey"
                :selected-date="selectedDateObj"
                class="absolute inset-0"
                @day-click="drillDown"
                @month-click="goToMonth"
              />
            </Transition>
          </div>
        </template>
      </main>
    </div>

    <!-- Overlays — always in DOM, animated in/out via Teleport -->
    <EventPopover />
    <BookingSlideOver />
    <CalendarSettingsPanel />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { pageTitle as buildPageTitle } from 'utils/pageTitle.js'
import { useCalendarStore }         from 'stores/calendar.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'
import { useGlobalStore }           from 'stores/global.js'
import { useCalendarChannel } from 'composables/useCalendarChannel.js'
import CalendarHeader    from './CalendarHeader.vue'
import CalendarSidebar   from './CalendarSidebar.vue'
import CalendarSearchResultsOverlay from './CalendarSearchResultsOverlay.vue'
import CalendarWeekView  from './CalendarWeekView.vue'
import CalendarDayView   from './CalendarDayView.vue'
import CalendarMonthView from './CalendarMonthView.vue'
import Calendar4DayView  from './Calendar4DayView.vue'
import CalendarYearView  from './CalendarYearView.vue'
import BookingSlideOver       from './BookingSlideOver.vue'
import EventPopover           from './EventPopover.vue'
import CalendarSettingsPanel  from './CalendarSettingsPanel.vue'

// No props — this component owns nothing. All state lives in the store.
const store         = useCalendarStore()
const settingsStore = useCalendarSettingsStore()
const globalStore   = useGlobalStore()
const mainEl        = ref(null)
const pageEl        = ref(null)
const { connect, disconnect } = useCalendarChannel()

const SIDEBAR_OPEN_KEY = 'calendar_sidebar_open'
const sidebarOpen = ref(localStorage.getItem(SIDEBAR_OPEN_KEY) !== 'false')

function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value
  localStorage.setItem(SIDEBAR_OPEN_KEY, String(sidebarOpen.value))
}

// ── Fullscreen ────────────────────────────────────────────────────────────────
// 'page' = whole calendar page (header + calendar sidebar, app sidebar excluded
// since it lives outside this component); 'main' = only the calendar grid.
const isFullscreen   = ref(false)
const fullscreenMode = ref(null) // 'page' | 'main' | null

function onFullscreen(mode) {
  if (mode === 'exit') {
    document.exitFullscreen?.()
    return
  }
  const el = mode === 'main' ? mainEl.value : pageEl.value
  el?.requestFullscreen?.().catch(() => {})
}

function onFullscreenChange() {
  const el = document.fullscreenElement
  isFullscreen.value   = !!el
  fullscreenMode.value = el ? (el === mainEl.value ? 'main' : 'page') : null
  // Overlays teleport to body normally; while fullscreen they must live inside
  // the fullscreen element or the browser will not render them.
  store.setOverlayTarget(el || 'body')
}

// Resizable sidebar
const SIDEBAR_WIDTH_KEY = 'calendar_sidebar_width'
const SIDEBAR_MIN = 180
const SIDEBAR_MAX = 420
const SIDEBAR_DEFAULT = 224

const sidebarWidth = ref(
  Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY)) || SIDEBAR_DEFAULT))
)

const isResizing = ref(false)
let _startX = 0
let _startWidth = 0

function startResize(e) {
  isResizing.value = true
  _startX = e.clientX
  _startWidth = sidebarWidth.value
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
}

function onResize(e) {
  if (!isResizing.value) return
  sidebarWidth.value = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, _startWidth + (e.clientX - _startX)))
}

function stopResize() {
  if (!isResizing.value) return
  isResizing.value = false
  localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth.value))
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
}

const selectedDateObj = computed(() => {
  return store.selectedDate ? new Date(store.selectedDate) : new Date()
})

// ── Document title ────────────────────────────────────────────────────────────
// Mirrors Google Calendar: the tab title reflects the current view and date.

function weekStartDate(date) {
  const startDay = Number(globalStore.user?.start_day_of_week ?? 0)
  const d = new Date(date)
  d.setDate(d.getDate() - ((d.getDay() - startDay + 7) % 7))
  return d
}

const pageTitle = computed(() => {
  const d = selectedDateObj.value
  const monthName = (date) => date.toLocaleDateString('en-US', { month: 'long' })

  switch (store.currentView) {
    case 'day':
      return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    case 'week': {
      const ws = weekStartDate(d)
      return `Week of ${ws.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
    }
    case '4days': {
      const end = new Date(d); end.setDate(d.getDate() + 3)
      const sameMonth = d.getMonth() === end.getMonth()
      const endPart = sameMonth ? `${end.getDate()}` : `${monthName(end)} ${end.getDate()}`
      return `${monthName(d)} ${d.getDate()} to ${endPart}, ${end.getFullYear()}`
    }
    case 'month':
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    case 'year':
      return String(d.getFullYear())
    default:
      return ''
  }
})

watch(pageTitle, (title) => {
  document.title = buildPageTitle(title, 'Calendar')
}, { immediate: true })

const navDirection = ref('forward') // 'forward' | 'backward'

function navigatePrev() {
  navDirection.value = 'backward'
  const d = new Date(selectedDateObj.value)
  if (store.currentView === 'day')   d.setDate(d.getDate() - 1)
  if (store.currentView === '4days') d.setDate(d.getDate() - 4)
  if (store.currentView === 'week')  d.setDate(d.getDate() - 7)
  if (store.currentView === 'month') d.setMonth(d.getMonth() - 1)
  if (store.currentView === 'year')  d.setFullYear(d.getFullYear() - 1)
  store.setDate(d)
}

function navigateNext() {
  navDirection.value = 'forward'
  const d = new Date(selectedDateObj.value)
  if (store.currentView === 'day')   d.setDate(d.getDate() + 1)
  if (store.currentView === '4days') d.setDate(d.getDate() + 4)
  if (store.currentView === 'week')  d.setDate(d.getDate() + 7)
  if (store.currentView === 'month') d.setMonth(d.getMonth() + 1)
  if (store.currentView === 'year')  d.setFullYear(d.getFullYear() + 1)
  store.setDate(d)
}

const monthKey = computed(() =>
  `month-${selectedDateObj.value.getFullYear()}-${selectedDateObj.value.getMonth()}`
)
const yearKey = computed(() =>
  `year-${selectedDateObj.value.getFullYear()}`
)

function navigateToday() {
  store.setDate(new Date())
}

function onSelectSearchResult(result) {
  store.setDate(new Date(result.start))
  store.setView('day')
  store.closeSearchOverlay()
}

function onSearchSubmit({ query, from_date, to_date }) {
  store.openSearchOverlay(query, { from_date, to_date })
}

// Filters changed while the full-results overlay is already open — refresh
// it in place. If the overlay isn't open (user is just typing in the header's
// autofill dropdown), the header refreshes that dropdown itself.
function onSearchFilterChange({ query, from_date, to_date }) {
  if (store.searchOverlayOpen) {
    store.openSearchOverlay(query, { from_date, to_date })
  }
}

function onEventClick(booking, anchorRect) {
  store.openEventPopover(booking, anchorRect)
}

function onSlotClick(date) {
  store.openBookingSlideOver(null)
  // Phase 6 will pre-fill the slide-over with the clicked date/time
}

function drillDown(date) {
  store.setDate(date)
  store.setView('day')
}

function goToMonth(date) {
  store.setDate(date)
  store.setView('month')
}

function retry() {
  store.invalidate()
  store.fetchInitialData()
}

// ── Scroll-to-navigate (month + year views only) ─────────────────────────────
const NAV_THRESHOLD = 30  // px delta required to trigger navigation
const NAV_COOLDOWN  = 400 // ms minimum between navigations
let _lastNavAt = 0

function onMainWheel(e) {
  if (!['month', 'year'].includes(store.currentView)) return

  const absDx = Math.abs(e.deltaX)
  const absDy = Math.abs(e.deltaY)
  const delta = Math.max(absDx, absDy)
  if (delta < NAV_THRESHOLD) return

  e.preventDefault()
  const now = Date.now()
  if (now - _lastNavAt < NAV_COOLDOWN) return
  _lastNavAt = now

  const isForward = absDx >= absDy ? e.deltaX > 0 : e.deltaY > 0
  if (isForward) navigateNext(); else navigatePrev()
}

onMounted(async () => {
  connect()
  document.addEventListener('fullscreenchange', onFullscreenChange)
  mainEl.value?.addEventListener('wheel', onMainWheel, { passive: false })

  // Apply cached default_view immediately (before API responds) to avoid visual flash
  if (!store.viewInitialized) {
    if (settingsStore.default_view) store.setView(settingsStore.default_view)
    store.viewInitialized = true
  }

  // Fetch fresh data in background — store updates reactively when they land
  await Promise.all([
    store.fetchInitialData(),
    settingsStore.fetchIfStale(),
    globalStore.refreshUser(),
  ])
})

onUnmounted(() => {
  disconnect()
  document.removeEventListener('fullscreenchange', onFullscreenChange)
  store.setOverlayTarget('body')
  mainEl.value?.removeEventListener('wheel', onMainWheel)
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
})
</script>

<style scoped>
/* ── Calendar slide transitions ── */
/* Forward (→ next month/year): new slides in from the right */
.cal-slide-forward-enter-from  { transform: translateX(100%); }
.cal-slide-forward-enter-active { transition: transform 280ms cubic-bezier(0.35, 0, 0.25, 1); }
.cal-slide-forward-enter-to    { transform: translateX(0); }
.cal-slide-forward-leave-from  { transform: translateX(0); }
.cal-slide-forward-leave-active { transition: transform 280ms cubic-bezier(0.35, 0, 0.25, 1); }
.cal-slide-forward-leave-to    { transform: translateX(-100%); }

/* Backward (← prev month/year): new slides in from the left */
.cal-slide-backward-enter-from  { transform: translateX(-100%); }
.cal-slide-backward-enter-active { transition: transform 280ms cubic-bezier(0.35, 0, 0.25, 1); }
.cal-slide-backward-enter-to    { transform: translateX(0); }
.cal-slide-backward-leave-from  { transform: translateX(0); }
.cal-slide-backward-leave-active { transition: transform 280ms cubic-bezier(0.35, 0, 0.25, 1); }
.cal-slide-backward-leave-to    { transform: translateX(100%); }
</style>
